// src/App.js
import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';

// Public Views
import PublicLayout from './views/PublicLayout';
import LandingPage from './views/LandingPage';
import FeaturesPage from './views/FeaturesPage'; 
import AuthPage from './views/AuthPage';
import PricingTable from './components/PricingTable';
import AboutPage from './views/AboutPage';
import FeatureDetailPage from './views/FeatureDetailPage';
import PrivacyPolicyPage from './views/PrivacyPolicyPage';
import TermsPage from './views/TermsPage';
import ComparisonPage from './views/ComparisonPage';
import PublicBookingPage from './views/PublicBookingPage';
import DeleteAccountPage from './views/DeleteAccountPage';
import SupportPage from './views/SupportPage';
import CookiePolicyPage from './views/CookiePolicyPage';

// Auth Handlers
import ResetPasswordPage from './views/ResetPasswordPage';

// Protected / App Views
import CustomerPortal from './views/CustomerPortal';
import PortalLogin from './views/PortalLogin';
import PortalPaymentResult from './views/PortalPaymentResult';
import ProtectedCrmLayout from './views/ProtectedCrmLayout';
import RequireAuth from './components/RequireAuth';
import OrgDeletedPage from './views/OrgDeletedPage';

// --- HELPER: Handle External Redirects ---
const ExternalRedirect = ({ to }) => {
  useEffect(() => {
    window.location.href = to;
  }, [to]);
  return <div className="min-h-screen bg-slate-900 flex items-center justify-center text-white">Redirecting to App...</div>;
};

function App() {
  // CRITICAL: This must be computed synchronously (not in a useEffect) so the
  // correct route tree renders on the very FIRST render pass. If initialized as
  // false and set in useEffect, the marketing routes render first and their
  // wildcard catch-all redirects any deep link (e.g. /settings?tab=payments)
  // to / then /home, wiping out query params before the app routes ever mount.
  const [isAppDomain] = useState(() => {
    // To preview the marketing site locally, start the server with:
    // REACT_APP_FORCE_MARKETING=true npm start
    if (process.env.REACT_APP_FORCE_MARKETING === 'true') return false;
    const hostname = window.location.hostname;
    return (
      hostname.startsWith('app.') ||
      hostname.includes('firebaseapp') ||
      hostname.includes('localhost')
    );
  });

  return (
    <HelmetProvider>
      <BrowserRouter>
        {isAppDomain ? (
          /* =========================================
             APP DOMAIN (app.spatchy.co)
             ========================================= */
          <Routes>
             <Route path="/" element={<Navigate to="/home" replace />} />
             <Route path="/book/:slug" element={<PublicBookingPage />} />
             <Route path="/login" element={<AuthPage />} />
             <Route path="/reset-password" element={<ResetPasswordPage />} />
             <Route path="/pricing" element={<PricingTable />} />
             
             <Route path="/portal/login/:token" element={<PortalLogin />} />
             <Route path="/portal/login" element={<PortalLogin />} />

             {/* Stripe redirect landing (read-only status; public — no session
                 needed). Registered BEFORE /portal/* and the catch-all so a
                 redirect return never falls through to the staff CRM. */}
             <Route path="/portal/payment-result" element={<PortalPaymentResult />} />

             <Route path="/portal/*" element={<CustomerPortal />} />

             {/* Deleted-org page — must be wrapped in RequireAuth so the
                 deleted-org gate inside RequireAuth allows access here.
                 Registered BEFORE the catch-all "/*" route below. */}
             <Route
               path="/org-deleted"
               element={
                 <RequireAuth>
                   <OrgDeletedPage />
                 </RequireAuth>
               }
             />

             <Route 
               path="/*" 
               element={
                 <RequireAuth>
                   <ProtectedCrmLayout />
                 </RequireAuth>
               } 
             />
          </Routes>
        ) : (
          /* =========================================
             MARKETING DOMAIN (spatchy.co)
             ========================================= */
          <Routes>
            {/* --- Public Booking Pages (no nav/footer, standalone) --- */}
            <Route path="/book/:slug" element={<PublicBookingPage />} />

            {/* --- Marketing Site (with nav/footer) --- */}
            <Route element={<PublicLayout />}>
               <Route path="/" element={<LandingPage />} />
               <Route path="/features" element={<FeaturesPage />} />
               
               <Route path="/features/:featureId" element={<FeatureDetailPage />} /> 
               
               <Route path="/about" element={<AboutPage />} />
               <Route path="/pricing" element={<PricingTable />} />

               {/* --- Legal Pages --- */}
               <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
               <Route path="/terms" element={<TermsPage />} />
               <Route path="/delete-account" element={<DeleteAccountPage />} />
               <Route path="/support" element={<SupportPage />} />
               <Route path="/cookie-policy" element={<CookiePolicyPage />} />

               {/* --- Comparison Pages --- */}
               <Route path="/compare/spatchy-vs-jobber" element={<ComparisonPage />} />
               
               <Route path="/login" element={<ExternalRedirect to="https://app.spatchy.co/login" />} />
             </Route>
             
             <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        )}
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;